import {Modal, modalManager, Typography} from '@innowise-group/ui-kit';
import {Employee, injectable, Workspace} from '@innowise-group/core';
import {Observable} from 'rxjs';
import {
  BookingWorkspaceForm,
  BookingWorkspaceValues,
  ExcludeDates,
  WorkspacesBookingList,
  ChooseOfficeFloorForm,
  ChooseOfficeFloorFormValues,
} from '@shared-components';
import {FloorsModalsFacade} from './floors-modals-facade.types';
import {FloorsContainerTypes} from '../floors-ioc.types';
import {UploadFloorForm, UploadFloorFormValues} from '../../components/upload-floor-form';
import {UploadFloorConfirmationForm} from '../../components/upload-floor-confirmation-form';
import {AddWorkspaceForm, AddWorkspaceValues} from '../../components/add-workspace-form';
import {EditRoomValues} from '../../components/edit-room-form/edit-room-form.component';
import {EditRoomForm} from '../../components/edit-room-form';
import {DeleteRoomConfirmationForm} from '../../components/delete-room-confirmation-form';
import {Dispatch, SetStateAction} from 'react';
import {BookMeetingRoomModal} from '../../components/book-meeting-room-modal';

@injectable()
class FloorsModalsFacadeService implements FloorsModalsFacade {
  public static readonly type = FloorsContainerTypes.FloorsModalsFacade;

  public openInfoModal(content: JSX.Element | string, title: string, button: string): void {
    modalManager.baseModal(typeof content === 'string' ? <Typography>{content}</Typography> : content, {title, button});
  }

  public openPlanUploadModal(handler: (values: UploadFloorFormValues) => void, title: string): void {
    modalManager.open(({close}) => (
      <Modal.Container>
        <Modal.Header title={title} onClose={close} />
        <Modal.Body>
          <UploadFloorForm onSubmit={handler} onCancel={close} />
        </Modal.Body>
      </Modal.Container>
    ));
  }
  public openConfirmationModal(
    title?: string,
    text?: string,
    confirmationText?: string,
    cancelText?: string,
    onComplete?: () => void,
  ): Observable<void> {
    return new Observable((observer) => {
      modalManager.confirmationModal({
        text,
        title,
        confirmationText,
        cancelText,
        onApprove: () => {
          observer.next();
        },
        onComplete: onComplete
          ? onComplete
          : () => {
              observer.complete();
            },
      });
    });
  }

  public openUploadConfirmationModal(
    title?: string,
    text?: string,
    confirmationText?: string,
    cancelText?: string,
  ): Observable<boolean> {
    return new Observable((observer) => {
      modalManager.open(({close}) => {
        const onHandlerSubmit = (values: UploadFloorFormValues) => {
          observer.next(values.toSaveArrangements);
          close();
        };
        return (
          <Modal.Container>
            <Modal.Header title={title} onClose={close} />
            <Modal.Body>{text && <Typography>{text}</Typography>}</Modal.Body>
            <Modal.Body>
              <UploadFloorConfirmationForm
                onSubmit={onHandlerSubmit}
                initialValues={{
                  toSaveArrangements: true,
                  confirmationText,
                  cancelText,
                  onCancel: close,
                }}
              />
            </Modal.Body>
          </Modal.Container>
        );
      });
    });
  }

  public openDeleteRoomConfirmationModal(
    title?: string,
    text?: string,
    confirmationText?: string,
    cancelText?: string,
  ): Observable<boolean> {
    return new Observable((observer) => {
      modalManager.open(({close}) => {
        const closeAndComplete = () => {
          if (!observer.closed) {
            observer.complete();
          }
          close();
        };
        const onHandlerSubmit = (values: {deleteAdjacentDoors: boolean}) => {
          observer.next(Boolean(values.deleteAdjacentDoors));
          observer.complete();
          close();
        };
        return (
          <Modal.Container>
            <Modal.Header title={title} onClose={closeAndComplete} />
            <Modal.Body>{text && <Typography>{text}</Typography>}</Modal.Body>
            <Modal.Body>
              <DeleteRoomConfirmationForm
                onSubmit={onHandlerSubmit}
                initialValues={{
                  deleteAdjacentDoors: false,
                  confirmationText,
                  cancelText,
                  onCancel: closeAndComplete,
                }}
              />
            </Modal.Body>
          </Modal.Container>
        );
      });
    });
  }

  public openWorkspaceActionsModal(
    handler: (values: AddWorkspaceValues) => void,
    officeId: string,
    initialValues?: Partial<AddWorkspaceValues>,
    title?: string,
    isVirtual?: boolean,
    datesToExclude?: ExcludeDates,
    revertCallback?: Dispatch<SetStateAction<Workspace[]>>,
  ): void {
    modalManager.open(({close}) => {
      const discardAndClose = () => {
        close();
      };
      const onHandlerSubmit = (values: AddWorkspaceValues) => {
        handler(values);
        close();
      };
      return (
        <Modal.Container>
          <Modal.Header title={title} onClose={discardAndClose} />
          <Modal.Body>
            <AddWorkspaceForm
              onSubmit={onHandlerSubmit}
              initialValues={initialValues}
              onCancel={discardAndClose}
              isVirtual={isVirtual}
              datesToExclude={datesToExclude}
              replaceEmployeeModal={this.openConfirmationModal.bind(this)}
              warningModal={this.openInfoModal.bind(this)}
              officeId={officeId}
              workspaceId={initialValues?.id}
              revertCallback={revertCallback}
            />
          </Modal.Body>
        </Modal.Container>
      );
    });
  }

  public openEditRoomFormModal(
    handler: (values: EditRoomValues) => Observable<unknown>,
    initialValues: Partial<EditRoomValues>,
    title: string,
    onDelete?: () => Observable<unknown>,
  ): void {
    modalManager.open(({close}) => (
      <Modal.Container>
        <Modal.Header title={title} onClose={close} />
        <Modal.Body>
          <EditRoomForm onSubmit={handler} onDelete={onDelete} initialValues={initialValues} onCancel={close} />
        </Modal.Body>
      </Modal.Container>
    ));
  }

  public openBookingWorkspaceForm(
    handler?: (values?: BookingWorkspaceValues) => Observable<unknown>,
    initialValues?: Partial<BookingWorkspaceValues>,
    title?: string,
    submitButton?: string,
    employee?: Employee,
    workspacesList?: WorkspacesBookingList,
  ): void {
    modalManager.open(({close}) => (
      <Modal.Container>
        <Modal.Header title={title} onClose={close} />
        <Modal.Body>
          <BookingWorkspaceForm
            submitButton={submitButton}
            initialValues={initialValues}
            onCancel={close}
            onSubmit={handler}
            employee={employee}
            workspacesList={workspacesList}
            confirmationModal={this.openConfirmationModal.bind(this)}
            withValidateBookedWorkspace
          />
        </Modal.Body>
      </Modal.Container>
    ));
  }

  public showSecondPlanForm(
    title?: string,
    permissions?: {[key: string]: number[]},
  ): Observable<ChooseOfficeFloorFormValues> {
    return new Observable((observer) => {
      modalManager.open(({close}) => {
        const onHandlerSubmit = (values: ChooseOfficeFloorFormValues) => {
          observer.next(values);
          observer.complete();
          close();
        };
        return (
          <Modal.Container>
            <Modal.Header title={title} onClose={close} />
            <Modal.Body>
              <ChooseOfficeFloorForm
                onCancel={close}
                onSubmit={onHandlerSubmit}
                submitName="show"
                permissions={permissions}
              />
            </Modal.Body>
          </Modal.Container>
        );
      });
    });
  }

  public openBookingMeetingRoomForm(meetingRoomId: string, title: string, floorAddress: string): void {
    modalManager.open(({close}) => {
      return (
        <BookMeetingRoomModal
          meetingRoomId={meetingRoomId}
          title={title}
          floorAddress={floorAddress}
          onClose={close}
          infoModal={this.openInfoModal.bind(this)}
        />
      );
    });
  }
}

export default FloorsModalsFacadeService;
